import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import trackedViewComponent from '../hoc/trackedViewComponent';
import dataComponent from '../hoc/dataComponent';
import collectibleEntity, { collectibleEntityPropTypes } from '../hoc/collectibleEntity';

import { loadProfile } from '../actions/profile';

import { includes, isEmpty } from 'lodash';
import urlJoin from 'url-join';
import * as Shapes from '../shapes';

import RedirectInstruction from '../lib/routing/RedirectInstruction';
import { ApiError } from '../middleware/api';
import { DataFetcherLoadingContext } from '../lib/routing/DataFetcher';

import {
  selectArtistIdFromSlug,
  selectProfile,
  selectProfileImageAnnotation,
} from '../selectors/profile';

import { ENTITY_TYPE_ARTIST } from '../constants';

import LoadingIndicator from '../components/common/LoadingIndicator';
import ProfileHead from '../components/profile/ProfileHead';
import ProfileNavigation from '../components/profile/ProfileNavigation';
import ProfileBanner from '../components/profile/ProfileBanner';
import CapacitorHeaderBar from '../components/capacitor/HeaderBar';
import styles from './Profile.css';
import * as analyticsActions from '../actions/analytics';
import shareableEntity, { shareableEntityPropTypes } from '../hoc/shareableEntity';

function checkShouldRedirectToAboutTab(profile) {
  return (
    profile.numEssentialsPlaylists > 0 ||
    profile.numConcerts > 0 ||
    profile.numLatestReleases > 0 ||
    profile.numRecommendedAlbums > 0
  );
}

function checkShouldShowAboutTab(profile) {
  return checkShouldRedirectToAboutTab(profile) || profile.similarComposersIds.length > 0;
}
class Profile extends PureComponent {
  static propTypes = {
    profile: Shapes.Profile.isRequired,
    profileIsComposer: PropTypes.bool.isRequired,
    profileHasPlaylists: PropTypes.bool.isRequired,
    children: PropTypes.object.isRequired,
    imageAnnotation: PropTypes.object,
    currentCursor: PropTypes.string,
    nextCursor: PropTypes.string,
    prevCursor: PropTypes.string,
    ...collectibleEntityPropTypes,
    ...shareableEntityPropTypes,
  };

  static onDataFetch(store, params, location) {
    const state = store.getState();
    const slugOrArtistId = params.slug;
    const profile = selectProfile(state, slugOrArtistId);
    const slugUrl = profile.slug;
    const pathname = location.pathname;
    const showAlbumsTab = profile.numAlbumsAsParticipant > 0;
    const showPlaylistsTab = profile.numPlaylists > 0;
    const isArtistId = /^\d+$/.test(slugOrArtistId);
    const defaultRoute = `profiles/${slugUrl}`;
    const isDefaultRoute =
      pathname === defaultRoute ||
      pathname === `${defaultRoute}/` ||
      pathname === `/${defaultRoute}`;
    const isNamespaced = includes(pathname, 'profiles/');
    if (isEmpty(profile)) {
      // selectProfile returns {} for non loaded profiles
      return Promise.reject(new ApiError(404, 'Not found', 'Profile not found'));
    }

    if (!isNamespaced) {
      return Promise.reject(new RedirectInstruction(urlJoin('/', 'profiles', pathname), true));
    }

    if (isArtistId || isDefaultRoute) {
      if (checkShouldRedirectToAboutTab(profile)) {
        return Promise.reject(new RedirectInstruction(`/profiles/${slugUrl}/about`, true));
      }
      if (showAlbumsTab) {
        return Promise.reject(new RedirectInstruction(`/profiles/${slugUrl}/albums`, true));
      }
      if (showPlaylistsTab) {
        return Promise.reject(new RedirectInstruction(`/profiles/${slugUrl}/playlists`, true));
      }
      return Promise.reject(new RedirectInstruction(`/profiles/${slugUrl}/recordings`, true));
    }

    return null;
  }

  render() {
    const {
      profile,
      profileIsComposer,
      profileHasPlaylists,
      children,
      imageAnnotation,
      isInCollection,
      shouldShowAboutTab,
      toggleIsInCollection,
      showShareModal,
    } = this.props;
    const displayLoadingIndicator = this.context && __CAPACITOR__;

    return (
      <div className="u-page-container">
        {__CAPACITOR__ && (
          <CapacitorHeaderBar
            showShareModal={showShareModal}
            title={profile.name}
            onlyShowTitleOnScroll
            isInCollection={isInCollection}
            toggleIsInCollection={toggleIsInCollection}
          />
        )}
        <ProfileBanner bannerImageUrl={profile.bannerImageUrl} />
        <ProfileHead
          profile={profile}
          imageAnnotation={imageAnnotation}
          isInCollection={isInCollection}
          toggleIsInCollection={toggleIsInCollection}
          showShareModal={showShareModal}
        />
        <ProfileNavigation
          slug={profile.slug}
          profileIsComposer={profileIsComposer}
          profileHasPlaylists={profileHasPlaylists}
          shouldShowAboutTab={shouldShowAboutTab}
        />
        <div className={styles.mainSectionWrapper}>
          {displayLoadingIndicator && (
            <div className={styles.loadingIndicator}>
              <LoadingIndicator isLoading />
            </div>
          )}
          <div>{children}</div>
        </div>
      </div>
    );
  }

  static contextType = DataFetcherLoadingContext;
}

function fetchData(store, { slug }) {
  return store.dispatch(loadProfile(slug));
}

function mapStateToProps(state, ownProps) {
  const { params, location, children } = ownProps;
  const { query, pathname } = location;
  if (!query) {
    // quick exit
    return {};
  }
  const { slug: slugOrArtistId } = params;
  const profile = selectProfile(state, slugOrArtistId);
  const profileIsComposer = !!profile.numRecordingsAsComposer;
  const profileHasPlaylists = profile.numPlaylists > 0;

  return {
    profile,
    pathname,
    profileIsComposer,
    profileHasPlaylists,
    shouldShowAboutTab: checkShouldShowAboutTab(profile),

    imageAnnotation: selectProfileImageAnnotation(state, slugOrArtistId),

    children,
  };
}

const trackingProps = (state, params) => {
  return {
    contextType: 'Profile',
    contextId: selectArtistIdFromSlug(state, params.slug),
  };
};

function getCollectibleEntityDescription(props) {
  return {
    id: props.profile.id,
    trackingSource: 'Profile',
  };
}

function getShareTrackingContext(props) {
  return {
    sharedContentType: 'Artist',
    sharedContentId: props.profile.id,
    contextType: 'Profile',
    contentId: props.profile.id,
  };
}

export default compose(
  trackedViewComponent('Profile', trackingProps),
  dataComponent(fetchData),
  connect(mapStateToProps, {
    track: analyticsActions.track,
  }),
  collectibleEntity(ENTITY_TYPE_ARTIST, getCollectibleEntityDescription),
  shareableEntity(ENTITY_TYPE_ARTIST, getShareTrackingContext, {
    sharedUrlParams: {
      utm_medium: 'profile-share',
    },
  })
)(Profile);
